import type {
  UserRepository,
  UserListOptions,
  UserPage,
  MyOrganizationsPage,
} from '#/domain/user/UserRepository'
import type { User, UserMetadata } from '#/domain/user/User'
import type { UserLog } from '#/domain/user/UserLog'
import type { Role } from '#/domain/role/Role'
import type { Application } from '#/domain/application/Application'
import type { HttpClientPort } from '#/infrastructure/persistence/HttpClientPort'
import {
  toDomain,
  toDomainPage,
  toDomainLogList,
  toDomainMyOrganizations,
  type UserResponseDto,
  type UserPageResponseDto,
  type UserLogResponseDto,
  type MyOrganizationsPageResponseDto,
} from './UserDto'
import {
  toDomainList as toDomainRoleList,
  type RoleResponseDto,
} from '#/infrastructure/implementation/role/RoleDto'
import {
  toDomainList as toDomainApplicationList,
  type ApplicationResponseDto,
} from '#/infrastructure/implementation/application/ApplicationDto'

export class HttpUserRepository implements UserRepository {
  constructor(private readonly http: HttpClientPort) {}

  async findAll(
    options: UserListOptions,
    search?: string,
    organizationId?: string,
    showInternal = true,
  ): Promise<UserPage> {
    const params: Record<string, string> = {
      page: String(options.page),
      itemsPerPage: String(options.itemsPerPage),
      showInternal: String(showInternal),
    }
    if (options.sortBy) params.sortBy = options.sortBy
    if (options.sortDesc) params.sortDesc = options.sortDesc
    if (search) params.search = search
    if (organizationId) params.organizationId = organizationId

    const raw = await this.http.get<UserPageResponseDto>('v1/auth0/user', {
      searchParams: new URLSearchParams(params),
    })
    return toDomainPage(raw)
  }

  async findById(userId: string, organizationId?: string): Promise<User> {
    const raw = await this.http.get<UserResponseDto>(`auth0/user/${userId}`, {
      searchParams: organizationId ? new URLSearchParams({ organizationId }) : undefined,
    })
    return toDomain(raw)
  }

  async findMyOrganizations(page: number, itemsPerPage: number): Promise<MyOrganizationsPage> {
    const raw = await this.http.get<MyOrganizationsPageResponseDto>('auth0/user/me/organizations', {
      searchParams: new URLSearchParams({ page: String(page), itemsPerPage: String(itemsPerPage) }),
    })
    return toDomainMyOrganizations(raw)
  }

  async updateMe(metadata: UserMetadata): Promise<void> {
    await this.http.patch<void>('auth0/user/me', { json: { UserMetadata: metadata } })
  }

  async findApplications(userId: string): Promise<Application[]> {
    const raw = await this.http.get<ApplicationResponseDto[]>(
      `v1/auth0/user/${encodeURIComponent(userId)}/applications`,
    )
    return toDomainApplicationList(raw)
  }

  async findLogs(userId: string, page = 0, perPage = 50): Promise<UserLog[]> {
    const raw = await this.http.get<UserLogResponseDto[]>(
      `v1/auth0/user/${encodeURIComponent(userId)}/logs`,
      { searchParams: new URLSearchParams({ page: String(page), perPage: String(perPage) }) },
    )
    return toDomainLogList(raw)
  }

  async findRoles(userId: string, organizationId?: string): Promise<Role[]> {
    const raw = await this.http.get<RoleResponseDto[]>(`v1/auth0/user/${userId}/roles`, {
      searchParams: organizationId ? new URLSearchParams({ organizationId }) : undefined,
    })
    return toDomainRoleList(raw)
  }

  async removeMember(userId: string, organizationId?: string): Promise<void> {
    await this.http.delete<void>(`v1/auth0/user/${userId}/member`, {
      searchParams: organizationId ? new URLSearchParams({ organizationId }) : undefined,
    })
  }

  async assignRoles(userId: string, roles: string[], organizationId?: string): Promise<void> {
    await this.http.post<void>(`v1/auth0/user/${userId}/roles`, {
      json: { Roles: roles },
      searchParams: organizationId ? new URLSearchParams({ organizationId }) : undefined,
    })
  }

  async removeRoles(userId: string, roles: string[], organizationId?: string): Promise<void> {
    await this.http.delete<void>(`v1/auth0/user/${userId}/roles`, {
      json: { Roles: roles },
      searchParams: organizationId ? new URLSearchParams({ organizationId }) : undefined,
    })
  }
}
